import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Tracker from "@/pages/Tracker";
import Admin from "@/pages/Admin";
import Inventory from "@/pages/Inventory";
import Planner from "@/pages/Planner";
import Profile from "@/pages/Profile";
import Billing from "@/pages/Billing";
import SignupInvite from "@/pages/SignupInvite";
import Onboarding from "@/pages/Onboarding";
import { Terms, Privacy } from "@/pages/Legal";
import TermsGate from "@/components/TermsGate";
import UpgradeLock from "@/components/UpgradeLock";
import "@/App.css";

// Capacitor deep-link handler: when the Android wrapper is opened via an Android App Link
// (e.g. https://tuha-preview.emergent.host/chat#session_id=xxx after Google OAuth bounces
// from Chrome), the WebView is NOT auto-navigated to that URL. We listen for the
// `appUrlOpen` event from @capacitor/app and push the path+search+hash into react-router so
// AppRouter's session_id-detection fires and signs the user in inside the app.
//
// Safe to no-op when running in a regular browser — the dynamic import resolves successfully
// (the package is web-compatible) and `addListener` simply never fires outside Capacitor.
function CapacitorDeepLinkBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    let cleanup;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const handle = await CapApp.addListener("appUrlOpen", (data) => {
          try {
            const incoming = data?.url || "";
            // OAuth return: either a custom-scheme link (tuha://auth#session_id=…) or an
            // https App Link (…/chat#session_id=…). In both cases route to /chat + hash so
            // AppRouter's session_id detection fires and AuthCallback signs the user in.
            const hashIdx = incoming.indexOf("#");
            const hash = hashIdx >= 0 ? incoming.slice(hashIdx) : "";
            if (hash.includes("session_id=")) {
              // OAuth return via the tuha:// custom scheme. The Capacitor JS bridge is present
              // (confirmed on device), so route into react-router to trigger AuthCallback.
              try { localStorage.setItem("tuha_aurl", incoming); } catch (_e) {}
              navigate("/chat" + hash, { replace: true });
              return;
            }
            const u = new URL(incoming);
            navigate(u.pathname + u.search + u.hash, { replace: true });
          } catch (_e) { /* ignore malformed URLs */ }
        });
        cleanup = () => handle.remove?.();
      } catch (_e) { /* package not available — running in pure web, ignore */ }
    })();
    return () => { if (cleanup) cleanup(); };
  }, [navigate]);
  return null;
}

// Native-only bootstrap: themes the Android status bar, dismisses the splash screen once the
// React app is mounted, and makes the hardware back button close any open overlay first, then
// navigate back, then exit at the home route. All dynamic imports no-op in a plain browser.
function NativeBootstrap() {
  useEffect(() => {
    let backHandle;
    (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: "#FAF8F5" });
        await StatusBar.setOverlaysWebView({ overlay: false });
      } catch (_e) { /* not native */ }
      try {
        const { SplashScreen } = await import("@capacitor/splash-screen");
        await SplashScreen.hide();
      } catch (_e) { /* not native */ }
      try {
        const { App: CapApp } = await import("@capacitor/app");
        backHandle = await CapApp.addListener("backButton", ({ canGoBack }) => {
          const overlay = document.querySelector(
            '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"]'
          );
          if (overlay) {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
            return;
          }
          if (window.location.pathname !== "/chat" && canGoBack) {
            window.history.back();
          } else {
            CapApp.exitApp();
          }
        });
      } catch (_e) { /* not native */ }
    })();
    return () => { backHandle?.remove?.(); };
  }, []);
  return null;
}

// Native-only AGT-16 push bootstrap: requests notification permission, registers the
// interactive action types, and syncs the daily nudge plan on launch + every app resume.
// No-ops in a plain browser / preview iframe (all Capacitor calls are guarded).
function NotificationBootstrap() {
  const navigate = useNavigate();
  useEffect(() => {
    let resumeHandle;
    (async () => {
      try {
        const { initNotifications, syncPlan } = await import("@/lib/notifications");
        const ok = await initNotifications(navigate);
        if (!ok) return; // not native, or permission denied
        await syncPlan();
        const { App: CapApp } = await import("@capacitor/app");
        resumeHandle = await CapApp.addListener("resume", () => { syncPlan(); });
      } catch (_e) { /* not native — ignore */ }
    })();
    return () => { resumeHandle?.remove?.(); };
  }, [navigate]);
  return null;
}

function Protected({ children, adminOnly = false, skipOnboarding = false, feature = null }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (!skipOnboarding && !user.profile?.onboarding_complete) return <Navigate to="/onboarding" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/chat" replace />;
  // Feature gating: Chat/Planner/Inventory need an active trial/plan; Tracker also stays
  // open during the 30-day post-signup window. When locked, show an upgrade screen.
  if (feature && !user.is_admin) {
    if (feature === "premium" && user.subscription_active === false) return <UpgradeLock kind="premium" />;
    if (feature === "tracker" && user.tracker_active === false) return <UpgradeLock kind="tracker" />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect session_id during render (not useEffect) to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/signup/:code" element={<SignupInvite />} />
      <Route path="/onboarding" element={<Protected skipOnboarding><Onboarding /></Protected>} />
      <Route path="/chat" element={<Protected feature="premium"><Dashboard /></Protected>} />
      <Route path="/tracker" element={<Protected feature="tracker"><Tracker /></Protected>} />
      <Route path="/inventory" element={<Protected feature="premium"><Inventory /></Protected>} />
      <Route path="/planner" element={<Protected feature="premium"><Planner /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CapacitorDeepLinkBridge />
          <NativeBootstrap />
          <NotificationBootstrap />
          <AppRouter />
          <TermsGate />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
